import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { getArticleBySlug } from '@/mocks/articles';
import type { SeoArticle } from '@/mocks/articles';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://hoobuy.boats';

function ArticleStructuredData({ article }: { article: SeoArticle }) {
  const articleUrl = `${siteUrl}/${article.slug}`;

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.subtitle,
    url: articleUrl,
    author: { '@type': 'Organization', name: article.author },
    datePublished: article.date,
    dateModified: '2026-06-25',
    inLanguage: 'pt',
    image: article.imageUrl,
    articleBody: article.sections.map((s) => s.heading + ' ' + s.body).join(' '),
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
    />
  );
}

function FaqStructuredData({ faq, articleSlug }: { faq: { question: string; answer: string }[]; articleSlug: string }) {
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    url: `${siteUrl}/${articleSlug}`,
    inLanguage: 'pt',
    mainEntity: faq.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
    />
  );
}

export default function BlogArticleLayout({ slug }: { slug: string }) {
  const article = getArticleBySlug(slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-28 pb-20 text-center">
          <h1 className="font-heading text-3xl font-bold text-foreground-950">Artigo não encontrado</h1>
          <Link to="/" className="text-primary-500 hover:underline mt-4 inline-block">Voltar ao início</Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50">
      <ArticleStructuredData article={article} />
      {article.faq.length > 0 && <FaqStructuredData faq={article.faq} articleSlug={article.slug} />}

      <Navbar />

      <article>
        <header className="relative pt-28 pb-16 md:pt-36 md:pb-24 px-4 md:px-6">
          <div className="max-w-3xl mx-auto">
            <span className="inline-block text-xs font-label font-semibold tracking-widest uppercase text-accent-500 bg-accent-100 px-3 py-1.5 rounded-full mb-5">
              {article.category}
            </span>

            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 leading-tight mb-5">
              {article.title}
            </h1>

            <p className="text-foreground-600 text-base md:text-lg leading-relaxed mb-6">
              {article.subtitle}
            </p>

            <div className="flex items-center gap-4 text-sm text-foreground-400 font-label">
              <span className="flex items-center gap-1.5">
                <i className="ri-user-line"></i>
                {article.author}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line"></i>
                {article.date}
              </span>
            </div>
          </div>
        </header>

        <div className="max-w-3xl mx-auto px-4 md:px-6 mb-12">
          <div className="rounded-2xl overflow-hidden bg-background-100 aspect-[16/9] md:aspect-[21/9]">
            <img
              src={article.imageUrl}
              alt={article.imageAlt}
              title={article.title}
              className="w-full h-full object-cover"
            />
          </div>
        </div>

        <div className="max-w-3xl mx-auto px-4 md:px-6 pb-8">
          <div className="prose prose-lg max-w-none text-foreground-700 leading-relaxed">
            <p className="text-foreground-600 text-base md:text-lg leading-relaxed mb-10">
              {article.intro}
            </p>

            {article.sections.map((section, idx) => (
              <section key={idx} className="mb-10">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
                  {section.heading}
                </h2>
                <p className="text-foreground-600 text-base leading-relaxed whitespace-pre-line">
                  {section.body}
                </p>
              </section>
            ))}

            <div className="bg-accent-100/60 border border-accent-200/50 rounded-2xl p-6 md:p-8 mb-10">
              <h3 className="font-heading text-xl font-bold text-foreground-950 mb-3">Conclusão</h3>
              <p className="text-foreground-600 text-base leading-relaxed">
                {article.conclusion}
              </p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row items-center gap-4 bg-background-100 rounded-2xl p-6 md:p-8 mb-12">
            <div className="flex-1">
              <p className="font-heading text-xl font-bold text-foreground-950 mb-2">
                Pronto para começar a economizar?
              </p>
              <p className="text-foreground-600 text-sm">
                Acesse o HoobuySpreadsheet e tenha acesso a todos os fornecedores verificados.
              </p>
            </div>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="bg-primary-500 text-background-50 dark:text-foreground-950 font-label font-semibold text-sm px-6 py-3 rounded-full hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              Acessar HoobuySpreadsheet
            </a>
          </div>

          {article.faq.length > 0 && (
            <section className="mb-12">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-6">
                Perguntas Frequentes
              </h2>
              <div className="space-y-3">
                {article.faq.map((item, idx) => (
                  <details key={idx} className="group bg-background-50 border border-background-200/70 rounded-xl overflow-hidden cursor-pointer">
                    <summary className="flex items-center justify-between px-5 py-4 font-label font-medium text-foreground-800 text-sm hover:text-primary-500 transition-colors duration-200">
                      {item.question}
                      <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform duration-200"></i>
                    </summary>
                    <div className="px-5 pb-4 text-foreground-600 text-sm leading-relaxed">
                      {item.answer}
                    </div>
                  </details>
                ))}
              </div>
            </section>
          )}
        </div>
      </article>

      <Footer />
    </div>
  );
}